import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Stack,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepIndicator,
  AspectRatio,
  IconButton,
  Chip,
  Divider
} from '@mui/joy';
import {
  Image as ImageIcon,
  Sparkle as GenerateIcon,
  ArrowLeft as BackIcon,
  ArrowRight as NextIcon,
  Check as CheckIcon,
  Plus as AddIcon,
  X as CloseIcon,
  ArrowsClockwise as RefreshIcon,
  Robot as AIIcon,
  Image as PhotoIcon,
  SkipForward as SkipIcon
} from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { TemplateSelector } from './TemplateSelector';
import { MediaLibrary } from './MediaLibrary';
import type {
  ImageTemplate,
  ImageTemplateCategory,
  TemplateDimension,
  TemplateContent,
  TemplateRenderResponse
} from '../../types/marketing';

// Simplified media type returned by render API
interface GeneratedMedia {
  id: number;
  url: string;
  thumbnail_url?: string | null;
  name?: string;
  file?: string;
  thumbnail?: string;
}

interface ImageComposerProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (media: GeneratedMedia) => void;
  defaultCategory?: ImageTemplateCategory;
  defaultContent?: Partial<TemplateContent>;
}

type ComposerStep = 'template' | 'content' | 'image' | 'preview';

const STEPS: { key: ComposerStep; label: string }[] = [
  { key: 'template', label: 'Select Template' },
  { key: 'content', label: 'Add Content' },
  { key: 'image', label: 'Select Image' },
  { key: 'preview', label: 'Preview & Generate' }
];

// Dimension display names
const DIMENSION_LABELS: Record<TemplateDimension, string> = {
  instagram_1080x1080: 'Instagram Square',
  facebook_1200x630: 'Facebook',
  instagram_1080x1920: 'Instagram Story',
  linkedin_1200x627: 'LinkedIn',
  twitter_1200x675: 'Twitter'
};

export const ImageComposer: React.FC<ImageComposerProps> = ({
  open,
  onClose,
  onGenerate,
  defaultCategory,
  defaultContent = {}
}) => {
  const queryClient = useQueryClient();

  // State
  const [currentStep, setCurrentStep] = useState<ComposerStep>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<ImageTemplate | null>(
    null
  );
  const [dimension, setDimension] = useState<TemplateDimension>(
    'instagram_1080x1080'
  );
  const [content, setContent] = useState<TemplateContent>(defaultContent);
  const [backgroundImageId, setBackgroundImageId] = useState<number | null>(
    null
  );
  const [beforeImageId, setBeforeImageId] = useState<number | null>(null);
  const [afterImageId, setAfterImageId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showMediaLibrary, setShowMediaLibrary] = useState<
    'background' | 'before' | 'after' | null
  >(null);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setCurrentStep('template');
      setSelectedTemplate(null);
      setDimension('instagram_1080x1080'); // Reset to square format
      setContent({});
      setBackgroundImageId(null);
      setBeforeImageId(null);
      setAfterImageId(null);
      setPreviewUrl(null);
    }
  }, [open]);

  // Apply default content when modal opens (separate effect to avoid infinite loops)
  const defaultContentRef = React.useRef(defaultContent);
  defaultContentRef.current = defaultContent;
  React.useEffect(() => {
    if (open && Object.keys(defaultContentRef.current).length > 0) {
      setContent(defaultContentRef.current);
    }
  }, [open]);

  // Preview mutation
  const previewMutation = useMutation<TemplateRenderResponse>({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error('No template selected');

      const response = await api.post<TemplateRenderResponse>(
        `/api/marketing/image-templates/${selectedTemplate.id}/preview/`,
        {
          dimension,
          content,
          background_image_id: backgroundImageId,
          before_image_id: beforeImageId,
          after_image_id: afterImageId
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success && data.media?.url) {
        setPreviewUrl(data.media.url);
      }
    }
  });

  // Generate mutation
  const generateMutation = useMutation<TemplateRenderResponse>({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error('No template selected');

      const response = await api.post<TemplateRenderResponse>(
        `/api/marketing/image-templates/${selectedTemplate.id}/render/`,
        {
          dimension,
          content,
          background_image_id: backgroundImageId,
          before_image_id: beforeImageId,
          after_image_id: afterImageId
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success && data.media) {
        queryClient.invalidateQueries({ queryKey: ['marketing-media'] });
        queryClient.invalidateQueries({ queryKey: ['combined-media'] });
        // Transform to the format expected by consumers (with file/thumbnail aliases)
        onGenerate({
          ...data.media,
          file: data.media.url,
          thumbnail: data.media.thumbnail_url || data.media.url
        });
        onClose();
      }
    }
  });

  // AI Content Generation mutation
  const generateContentMutation = useMutation<{ success: boolean; content: Partial<TemplateContent> }>({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error('No template selected');

      const response = await api.post<{ success: boolean; content: Partial<TemplateContent> }>(
        `/api/marketing/image-templates/${selectedTemplate.id}/generate-content/`,
        {}
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success && data.content) {
        setContent(prev => ({ ...prev, ...data.content }));
      }
    }
  });

  // Navigation
  const stepIndex = STEPS.findIndex((s) => s.key === currentStep);

  const canGoNext = () => {
    switch (currentStep) {
      case 'template':
        return selectedTemplate !== null;
      case 'content':
        return true; // Content is optional based on template
      case 'image':
        return true; // Image selection is optional
      case 'preview':
        return previewUrl !== null;
      default:
        return false;
    }
  };

  const goNext = () => {
    if (currentStep === 'template' && selectedTemplate) {
      setCurrentStep('content');
    } else if (currentStep === 'content') {
      setCurrentStep('image');
    } else if (currentStep === 'image') {
      setCurrentStep('preview');
      previewMutation.mutate();
    }
  };

  const goBack = () => {
    if (currentStep === 'content') {
      setCurrentStep('template');
    } else if (currentStep === 'image') {
      setCurrentStep('content');
    } else if (currentStep === 'preview') {
      setCurrentStep('image');
      setPreviewUrl(null);
    }
  };

  const handleTemplateSelect = (template: ImageTemplate) => {
    setSelectedTemplate(template);
    // Set dimension to first supported one if current isn't supported
    if (!template.supported_dimensions.includes(dimension)) {
      setDimension(template.supported_dimensions[0] as TemplateDimension);
    }
  };

  const handleMediaSelect = (media: any) => {
    const mediaId = typeof media.id === 'string'
      ? parseInt(media.id.replace('marketing_', '').replace('production_', ''))
      : media.id;

    if (showMediaLibrary === 'background') {
      setBackgroundImageId(mediaId);
    } else if (showMediaLibrary === 'before') {
      setBeforeImageId(mediaId);
    } else if (showMediaLibrary === 'after') {
      setAfterImageId(mediaId);
    }
    setShowMediaLibrary(null);
  };

  // Get content fields based on template category
  const getContentFields = () => {
    if (!selectedTemplate) return [];

    const fields: {
      key: keyof TemplateContent;
      label: string;
      type: 'text' | 'textarea' | 'number' | 'list';
      placeholder?: string;
    }[] = [];

    switch (selectedTemplate.category) {
      case 'testimonial':
        fields.push(
          {
            key: 'quote',
            label: 'Quote Text',
            type: 'textarea',
            placeholder: 'Enter the testimonial quote...'
          },
          {
            key: 'reviewer_name',
            label: 'Reviewer Name',
            type: 'text',
            placeholder: 'John D.'
          },
          { key: 'rating', label: 'Star Rating (1-5)', type: 'number' }
        );
        break;
      case 'company_post':
        fields.push(
          {
            key: 'headline',
            label: 'Headline',
            type: 'text',
            placeholder: 'Your headline here...'
          },
          {
            key: 'body_text',
            label: 'Body Text',
            type: 'textarea',
            placeholder: 'Additional text...'
          },
          {
            key: 'cta_text',
            label: 'Call-to-Action',
            type: 'text',
            placeholder: 'Call Today!'
          }
        );
        break;
      case 'before_after':
        fields.push(
          {
            key: 'headline',
            label: 'Headline',
            type: 'text',
            placeholder: 'Transformation Complete!'
          },
          {
            key: 'body_text',
            label: 'Description',
            type: 'textarea',
            placeholder: 'See the difference...'
          }
        );
        break;
      case 'promotion':
        fields.push(
          {
            key: 'headline',
            label: 'Headline',
            type: 'text',
            placeholder: 'Special Offer!'
          },
          {
            key: 'body_text',
            label: 'Offer Details',
            type: 'textarea',
            placeholder: 'Get a free inspection...'
          },
          {
            key: 'cta_text',
            label: 'Call-to-Action',
            type: 'text',
            placeholder: 'Book Now'
          }
        );
        break;
      case 'service':
        fields.push(
          {
            key: 'headline',
            label: 'Headline',
            type: 'text',
            placeholder: 'Our Services'
          }
        );
        break;
    }

    return fields;
  };

  // Check if template needs images
  const needsBackgroundImage = () => {
    if (!selectedTemplate) return false;
    const config = selectedTemplate.layer_config?.layers || [];
    return config.some(
      (l: any) =>
        l.type === 'background' && l.config?.type === 'image' ||
        l.type === 'image_placeholder' && l.config?.field === 'background_image'
    );
  };

  const needsBeforeAfterImages = () => {
    return selectedTemplate?.category === 'before_after';
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog
          size="lg"
          sx={{
            width: '95%',
            maxWidth: 1100,
            maxHeight: '95vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ModalClose />
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon />
              Create Branded Image
            </Box>
          </DialogTitle>

          {/* Stepper */}
          <Box sx={{ px: 2, py: 1 }}>
            <Stepper sx={{ width: '100%' }}>
              {STEPS.map((step, index) => (
                <Step
                  key={step.key}
                  indicator={
                    <StepIndicator
                      variant={index <= stepIndex ? 'solid' : 'outlined'}
                      color={index <= stepIndex ? 'primary' : 'neutral'}
                    >
                      {index < stepIndex ? (
                        <CheckIcon />
                      ) : (
                        index + 1
                      )}
                    </StepIndicator>
                  }
                >
                  {step.label}
                </Step>
              ))}
            </Stepper>
          </Box>

          <Divider />

          <DialogContent sx={{ overflow: 'auto', flex: 1 }}>
            {/* Step 1: Template Selection */}
            {currentStep === 'template' && (
              <Box>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Choose a Template
                </Typography>
                <TemplateSelector
                  selectedTemplateId={selectedTemplate?.id || null}
                  onSelect={handleTemplateSelect}
                  category={defaultCategory || 'all'}
                  showCategoryFilter={!defaultCategory}
                  showDimensionFilter
                  dimension={dimension}
                  onDimensionChange={setDimension}
                />
              </Box>
            )}

            {/* Step 2: Content Input */}
            {currentStep === 'content' && selectedTemplate && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2
                  }}
                >
                  <Chip variant="soft" color="primary">
                    {selectedTemplate.name}
                  </Chip>
                  <Chip variant="outlined">
                    {DIMENSION_LABELS[dimension]}
                  </Chip>
                </Box>

                {/* AI Content Generation Card */}
                <Card variant="soft" color="primary" sx={{ mb: 3 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography level="title-sm" startDecorator={<AIIcon />}>
                          AI Content Generation
                        </Typography>
                        <Typography level="body-xs">
                          Generate content based on your template type
                        </Typography>
                      </Box>
                      <Button
                        size="sm"
                        loading={generateContentMutation.isPending}
                        onClick={() => generateContentMutation.mutate()}
                        startDecorator={!generateContentMutation.isPending ? <RefreshIcon /> : undefined}
                      >
                        {generateContentMutation.isPending ? 'Generating...' : 'Auto-Generate'}
                      </Button>
                    </Box>
                    {generateContentMutation.isError && (
                      <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                        {(generateContentMutation.error as any)?.response?.data?.error ||
                          'Failed to generate content'}
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                <Stack spacing={3}>
                  {/* Text Fields */}
                  {getContentFields().map((field) => (
                    <FormControl key={field.key}>
                      <FormLabel>{field.label}</FormLabel>
                      {field.type === 'textarea' ? (
                        <Textarea
                          value={(content[field.key] as string) || ''}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              [field.key]: e.target.value
                            })
                          }
                          placeholder={field.placeholder}
                          minRows={3}
                        />
                      ) : field.type === 'number' ? (
                        <Input
                          type="number"
                          value={(content[field.key] as number) || ''}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              [field.key]: parseInt(e.target.value) || 0
                            })
                          }
                          slotProps={{
                            input: { min: 1, max: 5 }
                          }}
                        />
                      ) : (
                        <Input
                          value={(content[field.key] as string) || ''}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              [field.key]: e.target.value
                            })
                          }
                          placeholder={field.placeholder}
                        />
                      )}
                    </FormControl>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Step 3: Image Selection (Optional) */}
            {currentStep === 'image' && selectedTemplate && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2
                  }}
                >
                  <Chip variant="soft" color="primary">
                    {selectedTemplate.name}
                  </Chip>
                  <Chip variant="outlined">
                    {DIMENSION_LABELS[dimension]}
                  </Chip>
                </Box>

                <Card variant="soft" color="neutral" sx={{ mb: 3 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhotoIcon style={{ opacity: 0.7 }} />
                      <Box>
                        <Typography level="title-sm">
                          Add a Background Image (Optional)
                        </Typography>
                        <Typography level="body-xs">
                          Select an image from your media library, or skip to use the template's default gradient background
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Stack spacing={3}>
                  {/* Background Image */}
                  <FormControl>
                    <FormLabel>Background Image</FormLabel>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      {backgroundImageId ? (
                        <Chip
                          variant="soft"
                          color="success"
                          endDecorator={
                            <IconButton
                              size="sm"
                              variant="plain"
                              onClick={() => setBackgroundImageId(null)}
                            >
                              <CloseIcon size={18} />
                            </IconButton>
                          }
                        >
                          Image Selected
                        </Chip>
                      ) : (
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                          No image selected
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        color="primary"
                        startDecorator={<AddIcon />}
                        onClick={() => setShowMediaLibrary('background')}
                      >
                        {backgroundImageId ? 'Change Image' : 'Select from Library'}
                      </Button>
                    </Box>
                  </FormControl>

                  {/* Before/After Images for comparison templates */}
                  {needsBeforeAfterImages() && (
                    <>
                      <Divider />
                      <Typography level="title-sm">
                        Before & After Comparison Images
                      </Typography>

                      <FormControl>
                        <FormLabel>Before Image</FormLabel>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          {beforeImageId ? (
                            <Chip
                              variant="soft"
                              color="success"
                              endDecorator={
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  onClick={() => setBeforeImageId(null)}
                                >
                                  <CloseIcon size={18} />
                                </IconButton>
                              }
                            >
                              Before Image Selected
                            </Chip>
                          ) : (
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                              No image selected
                            </Typography>
                          )}
                          <Button
                            variant="outlined"
                            color="primary"
                            startDecorator={<AddIcon />}
                            onClick={() => setShowMediaLibrary('before')}
                          >
                            {beforeImageId ? 'Change' : 'Select Before Image'}
                          </Button>
                        </Box>
                      </FormControl>

                      <FormControl>
                        <FormLabel>After Image</FormLabel>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          {afterImageId ? (
                            <Chip
                              variant="soft"
                              color="success"
                              endDecorator={
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  onClick={() => setAfterImageId(null)}
                                >
                                  <CloseIcon size={18} />
                                </IconButton>
                              }
                            >
                              After Image Selected
                            </Chip>
                          ) : (
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                              No image selected
                            </Typography>
                          )}
                          <Button
                            variant="outlined"
                            color="primary"
                            startDecorator={<AddIcon />}
                            onClick={() => setShowMediaLibrary('after')}
                          >
                            {afterImageId ? 'Change' : 'Select After Image'}
                          </Button>
                        </Box>
                      </FormControl>
                    </>
                  )}
                </Stack>
              </Box>
            )}

            {/* Step 4: Preview & Generate */}
            {currentStep === 'preview' && (
              <Box>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Preview
                </Typography>

                {previewMutation.isPending && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      py: 6
                    }}
                  >
                    <CircularProgress size="lg" />
                    <Typography sx={{ mt: 2 }}>
                      Generating preview...
                    </Typography>
                  </Box>
                )}

                {previewMutation.isError && (
                  <Alert color="danger" sx={{ mb: 2 }}>
                    {(previewMutation.error as any)?.response?.data?.error ||
                      'Failed to generate preview'}
                  </Alert>
                )}

                {previewUrl && (
                  <Card variant="outlined" sx={{ p: 0, overflow: 'hidden', maxWidth: 400, mx: 'auto' }}>
                    <AspectRatio
                      ratio={
                        dimension === 'instagram_1080x1080'
                          ? '1'
                          : dimension === 'instagram_1080x1920'
                          ? '9/16'
                          : '16/9'
                      }
                    >
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="Preview"
                        sx={{ objectFit: 'contain' }}
                      />
                    </AspectRatio>
                  </Card>
                )}

                {generateMutation.isError && (
                  <Alert color="danger" sx={{ mt: 2 }}>
                    {(generateMutation.error as any)?.response?.data?.error ||
                      'Failed to generate image'}
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>

          <Divider />

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              variant="plain"
              startDecorator={<BackIcon />}
              onClick={stepIndex === 0 ? onClose : goBack}
            >
              {stepIndex === 0 ? 'Cancel' : 'Back'}
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {currentStep === 'preview' && previewUrl && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => previewMutation.mutate()}
                  disabled={previewMutation.isPending}
                >
                  Regenerate Preview
                </Button>
              )}

              {currentStep !== 'preview' ? (
                <Button
                  endDecorator={currentStep === 'image' && !backgroundImageId && !beforeImageId && !afterImageId ? <SkipIcon /> : <NextIcon />}
                  onClick={goNext}
                  disabled={!canGoNext()}
                >
                  {currentStep === 'image'
                    ? (backgroundImageId || beforeImageId || afterImageId)
                      ? 'Continue to Preview'
                      : 'Skip & Preview'
                    : 'Next'
                  }
                </Button>
              ) : (
                <Button
                  startDecorator={
                    generateMutation.isPending ? (
                      <CircularProgress size="sm" />
                    ) : (
                      <GenerateIcon />
                    )
                  }
                  onClick={() => generateMutation.mutate()}
                  disabled={!previewUrl || generateMutation.isPending}
                  loading={generateMutation.isPending}
                >
                  Generate & Save
                </Button>
              )}
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <Modal open={!!showMediaLibrary} onClose={() => setShowMediaLibrary(null)}>
          <ModalDialog size="lg" sx={{ width: '90%', maxWidth: 1000, maxHeight: '90vh' }}>
            <ModalClose />
            <DialogTitle>
              Select {showMediaLibrary === 'background' ? 'Background' : showMediaLibrary === 'before' ? 'Before' : 'After'} Image
            </DialogTitle>
            <DialogContent>
              <MediaLibrary
                selectionMode={true}
                onSelect={handleMediaSelect}
              />
            </DialogContent>
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default ImageComposer;
